import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MapPin, Check, Users, CalendarDays, Globe, PlaneTakeoff } from "lucide-react";
import { toast } from "sonner";
import api, { fcfa, formatApiError, resolveImg, offerFromPrice } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function parseDestination(dest) {
  const parts = (dest || "").split(",").map((s) => s.trim());
  if (parts.length >= 2) return { city: parts[0], country: parts[parts.length - 1] };
  return { city: parts[0] || "", country: "" };
}

export default function OfferDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [offer, setOffer] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [departureCountry, setDepartureCountry] = useState("");
  const [destCountry, setDestCountry] = useState("");
  const [destCity, setDestCity] = useState("");
  const [departDate, setDepartDate] = useState("");
  const [returnDate, setReturnDate] = useState("");
  const [numPeople, setNumPeople] = useState(1);

  useEffect(() => {
    api.get(`/offers/${id}`).then(({ data }) => {
      setOffer(data);
      const dps = data.departure_prices || [];
      setDepartureCountry(dps[0]?.country || "");
      const { city, country } = parseDestination(data.destination);
      setDestCity(city);
      setDestCountry(country);
    }).catch(() => navigate("/offers"));
  }, [id, navigate]);

  if (!offer) {
    return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-4 border-accent border-t-transparent animate-spin" /></div>;
  }

  const dps = offer.departure_prices || [];
  const unitPrice = (() => {
    const m = dps.find((d) => d.country === departureCountry);
    return m ? m.price : offer.price_per_person;
  })();
  const total = unitPrice * numPeople;

  const openReserve = () => {
    if (!user) { toast.info("Connectez-vous pour réserver"); navigate("/login"); return; }
    if (user.role === "admin") { toast.info("Les réservations se font avec un compte client."); return; }
    setOpen(true);
  };

  const confirmReserve = async () => {
    if (dps.length && !departureCountry) { toast.error("Choisissez votre pays de départ"); return; }
    if (!destCountry || !destCity) { toast.error("Renseignez le pays et la ville de destination"); return; }
    if (!departDate) { toast.error("Choisissez une date de départ"); return; }
    if (returnDate && returnDate < departDate) { toast.error("La date de retour doit être après le départ"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings", {
        offer_id: offer.id,
        departure_country: departureCountry || "Non précisé",
        destination_country: destCountry,
        destination_city: destCity,
        depart_date: departDate,
        return_date: returnDate,
        num_people: Number(numPeople),
      });
      toast.success("Réservation créée !");
      setOpen(false);
      navigate(`/client/payment/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="grid lg:grid-cols-2 gap-8">
        <div>
          <div className="rounded-3xl overflow-hidden soft-shadow mb-3 bg-secondary">
            {offer.photos?.length ? (
              <img src={resolveImg(offer.photos[activePhoto])} alt={offer.title} className="w-full h-80 sm:h-96 object-cover" data-testid="offer-main-photo" />
            ) : (
              <div className="w-full h-80 sm:h-96 flex items-center justify-center text-muted-foreground"><Globe className="h-12 w-12" /></div>
            )}
          </div>
          {offer.photos?.length > 1 && (
            <div className="flex gap-3 flex-wrap">
              {offer.photos.map((p, i) => (
                <button key={i} onClick={() => setActivePhoto(i)} className={`h-20 w-28 rounded-xl overflow-hidden border-2 ${activePhoto === i ? "border-accent" : "border-transparent"}`} data-testid={`offer-thumb-${i}`}>
                  <img src={resolveImg(p)} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="flex items-center gap-1 text-muted-foreground text-sm mb-2"><MapPin className="h-4 w-4" /> {offer.destination}</div>
          <h1 className="font-head text-3xl sm:text-4xl font-extrabold text-primary leading-tight mb-4" data-testid="offer-title">{offer.title}</h1>
          <p className="text-muted-foreground leading-relaxed mb-6">{offer.description}</p>

          <div className="bg-white rounded-2xl soft-shadow border border-gray-100 p-5 mb-5">
            <div className="text-sm text-muted-foreground">Prix par personne</div>
            <div className="font-head text-3xl font-extrabold text-accent mb-4" data-testid="offer-price">à partir de {fcfa(offerFromPrice(offer))}</div>

            {dps.length > 0 && (
              <>
                <div className="text-sm font-semibold text-primary flex items-center gap-1 mb-2"><PlaneTakeoff className="h-4 w-4" /> Tarif selon le pays de départ</div>
                <div className="space-y-1.5 mb-4" data-testid="departure-prices-list">
                  {dps.map((d) => (
                    <div key={d.country} className="flex items-center justify-between text-sm bg-secondary rounded-lg px-3 py-1.5">
                      <span className="text-primary font-medium">{d.country}</span>
                      <span className="font-bold text-accent">{fcfa(d.price)}</span>
                    </div>
                  ))}
                </div>
              </>
            )}

            <Button onClick={openReserve} className="w-full h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 text-base font-bold" data-testid="reserve-btn">
              Réserver
            </Button>
          </div>

          <ul className="space-y-2 text-sm text-primary/80">
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" /> Choisissez votre pays de départ et vos dates</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" /> Paiement accompagné par notre équipe</li>
            <li className="flex items-center gap-2"><Check className="h-4 w-4 text-accent" /> Assistance sur WhatsApp</li>
          </ul>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="reserve-dialog">
          <DialogHeader><DialogTitle className="font-head">Réserver — {offer.title}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-semibold text-primary flex items-center gap-1 mb-1"><PlaneTakeoff className="h-4 w-4" /> Pays de départ</label>
              {dps.length ? (
                <select value={departureCountry} onChange={(e) => setDepartureCountry(e.target.value)} className="w-full h-11 rounded-xl border border-input px-3 bg-white" data-testid="reserve-departure-country">
                  {dps.map((d) => <option key={d.country} value={d.country}>{d.country} — {fcfa(d.price)}</option>)}
                </select>
              ) : (
                <Input value={departureCountry} onChange={(e) => setDepartureCountry(e.target.value)} placeholder="Votre pays de départ" className="rounded-xl" data-testid="reserve-departure-country" />
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-primary flex items-center gap-1 mb-1"><Globe className="h-4 w-4" /> Pays de destination</label>
                <Input value={destCountry} onChange={(e) => setDestCountry(e.target.value)} className="rounded-xl" data-testid="reserve-dest-country" />
              </div>
              <div>
                <label className="text-sm font-semibold text-primary flex items-center gap-1 mb-1"><MapPin className="h-4 w-4" /> Ville de destination</label>
                <Input value={destCity} onChange={(e) => setDestCity(e.target.value)} className="rounded-xl" data-testid="reserve-dest-city" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-semibold text-primary flex items-center gap-1 mb-1"><CalendarDays className="h-4 w-4" /> Date de départ</label>
                <Input type="date" value={departDate} onChange={(e) => setDepartDate(e.target.value)} className="rounded-xl" data-testid="reserve-depart-date" />
              </div>
              <div>
                <label className="text-sm font-semibold text-primary flex items-center gap-1 mb-1"><CalendarDays className="h-4 w-4" /> Date de retour <span className="text-muted-foreground font-normal">(opt.)</span></label>
                <Input type="date" value={returnDate} onChange={(e) => setReturnDate(e.target.value)} className="rounded-xl" data-testid="reserve-return-date" />
              </div>
            </div>

            <div>
              <label className="text-sm font-semibold text-primary flex items-center gap-1 mb-1"><Users className="h-4 w-4" /> Nombre de personnes</label>
              <Input type="number" min={1} value={numPeople} onChange={(e) => setNumPeople(Math.max(1, Number(e.target.value)))} className="rounded-xl" data-testid="reserve-people-input" />
            </div>

            <div className="bg-secondary rounded-xl p-4 flex items-center justify-between">
              <span className="font-semibold text-primary">Total à payer</span>
              <span className="font-head text-2xl font-extrabold text-accent" data-testid="reserve-total">{fcfa(total)}</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={confirmReserve} disabled={submitting} className="w-full h-12 rounded-full bg-accent text-accent-foreground hover:bg-accent/90 font-bold" data-testid="confirm-reserve-btn">
              {submitting ? "Création..." : "Confirmer la réservation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
